// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { DashMiddleware, DashNext, DashStore } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors, initActionTypes } from '@makemydeal/dr-shared-store';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { TradeInIntelligenceProxyOutcome } from '@makemydeal/dr-dash-bff-types';

// actions
import {
    requestTradeInIntelligence,
    tradeInIntelligenceRequestAccepted,
    tradeInIntelligenceRequested,
    tradeInIntelligenceSettledEmpty
} from '../actions/tradeInIntelligenceActionCreators';

// consts/enums
import {
    TRADE_IN_INTELLIGENCE_EVENT_RECEIVED,
    TRADE_IN_INTELLIGENCE_FETCH_FAILURE,
    TRADE_IN_INTELLIGENCE_FETCH_SUCCESS,
    TRADE_IN_INTELLIGENCE_SUBSCRIBED
} from '../actionTypes/tradeInIntelligenceActionTypes';

// selectors
import { getDealXgId } from '../selectors/offerRedux';
import { getOfferGlobalCustomerId } from '../selectors/offerSelectors';
import { getPrimaryScopeState } from '../selectors/primaryScopeSelectors';
import * as tradeInIntelligenceSelectors from '../selectors/tradeInIntelligenceSelectors';

const { INIT_SUCCESS } = initActionTypes;

export const TRADE_IN_INTELLIGENCE_GIVE_UP_MS = 75_000;

let isDealInitialized = false;
let isTopicSubscribed = false;
let hasLoggedSkip = false;
let lastDisplaySignature: string | null = null;
let giveUpTimer: ReturnType<typeof setTimeout> | null = null;

export function __resetTradeInIntelligenceState(): void {
    if (giveUpTimer) {
        clearTimeout(giveUpTimer);
    }
    giveUpTimer = null;
    isDealInitialized = false;
    isTopicSubscribed = false;
    hasLoggedSkip = false;
    lastDisplaySignature = null;
}

const clearGiveUpTimer = () => {
    if (giveUpTimer) {
        clearTimeout(giveUpTimer);
        giveUpTimer = null;
    }
};

const isEnabled = (state: any): boolean =>
    featureToggleSelectors.areFeatureTogglesAvailable(state) &&
    featureToggleSelectors.isTradeInIntelligenceEnabled(state) &&
    featureToggleSelectors.isShowDealPulseInManagerViewEnabled(state);

const logResolved = (finalState: string, recordCount: number, requestToRenderDelayMs: number | null) =>
    logNewRelicPageAction('MV:TradeInIntelligence:Resolved', { finalState, recordCount, requestToRenderDelayMs });

const settleEmpty = (store: DashStore, finalState: string) => {
    clearGiveUpTimer();
    if (!tradeInIntelligenceSelectors.getIsTradeInIntelligencePending(store.getState())) {
        return;
    }
    store.dispatch(tradeInIntelligenceSettledEmpty());
    logResolved(finalState, 0, null);
};

const logDisplayChange = (store: DashStore) => {
    const primaryState = getPrimaryScopeState(store.getState());
    const display = tradeInIntelligenceSelectors.getTradeInIntelligenceDisplay(primaryState);
    if (!display) {
        return;
    }
    const signature = [display.resolution, display.reason, display.matchMethod, display.shownRank].join('|');
    if (signature === lastDisplaySignature) {
        return;
    }
    const trigger = lastDisplaySignature === null ? 'initial' : 'trade_change';
    lastDisplaySignature = signature;
    logNewRelicPageAction('MV:TradeInIntelligence:Displayed', { ...display, dealXgId: getDealXgId(primaryState), trigger });
};

const requestWhenReady = (store: DashStore) => {
    const state = store.getState();
    if (!isDealInitialized || !isTopicSubscribed || tradeInIntelligenceSelectors.getTradeInIntelligence(state).status !== 'idle') {
        return;
    }
    if (!isEnabled(state)) {
        return;
    }
    if (!getOfferGlobalCustomerId(state) || !getDealXgId(state)) {
        if (!hasLoggedSkip) {
            hasLoggedSkip = true;
            logNewRelicPageAction('MV:TradeInIntelligence:Skipped', { reason: 'customer_unidentified' });
        }
        return;
    }

    store.dispatch(tradeInIntelligenceRequested(Date.now()));
    store.dispatch(requestTradeInIntelligence(store.getState()) as unknown as AnyFSA);
    logNewRelicPageAction('MV:TradeInIntelligence:Requested', {});

    clearGiveUpTimer();
    giveUpTimer = setTimeout(() => {
        giveUpTimer = null;
        if (tradeInIntelligenceSelectors.getIsTradeInIntelligencePending(store.getState())) {
            logNewRelicPageAction('MV:TradeInIntelligence:TimedOut', {});
            settleEmpty(store, 'timed_out');
        }
    }, TRADE_IN_INTELLIGENCE_GIVE_UP_MS);
};

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    next(action);

    switch (action.type) {
        case INIT_SUCCESS: {
            isDealInitialized = true;
            requestWhenReady(store);
            break;
        }

        case TRADE_IN_INTELLIGENCE_SUBSCRIBED: {
            isTopicSubscribed = true;
            requestWhenReady(store);
            break;
        }

        case offerReduxActionTypes.SWAPPED_GLOBAL_CUSTOMER_IDS: {
            hasLoggedSkip = false;
            requestWhenReady(store);
            break;
        }

        case TRADE_IN_INTELLIGENCE_FETCH_SUCCESS: {
            const outcome = action.payload?.outcome as TradeInIntelligenceProxyOutcome | undefined;
            if (outcome === TradeInIntelligenceProxyOutcome.ACCEPTED) {
                store.dispatch(tradeInIntelligenceRequestAccepted(action.payload.correlationId ?? null));
                break;
            }
            settleEmpty(store, outcome ?? 'unknown');
            break;
        }

        case TRADE_IN_INTELLIGENCE_FETCH_FAILURE: {
            settleEmpty(store, 'request_failed');
            break;
        }

        case TRADE_IN_INTELLIGENCE_EVENT_RECEIVED: {
            clearGiveUpTimer();
            const slice = tradeInIntelligenceSelectors.getTradeInIntelligence(store.getState());
            logResolved(
                action.payload?.state ?? 'unknown',
                slice.records.length,
                slice.requestedAtMs === null ? null : Date.now() - slice.requestedAtMs
            );
            break;
        }

        default:
            break;
    }

    logDisplayChange(store);
};
